import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import ChangePasswordForm from "./ChangePasswordForm";
import {
  LayoutDashboard, Wand2, Image, Users, CalendarDays,
  CalendarRange, FileText, Library, History, Settings as Cog, LogOut, Compass, Plane,
  Menu, UserCog,
} from "lucide-react";

function daysTo(dep) {
  if (!dep) return null;
  const d = new Date(dep + "T00:00:00");
  return Math.round((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
}

const NAV = [
  { to: "/", label: "Today", icon: LayoutDashboard, testid: "nav-today" },
  { to: "/create", label: "Create", icon: Wand2, testid: "nav-create" },
  { to: "/media", label: "Media Library", icon: Image, testid: "nav-media" },
  { to: "/leads", label: "Follow-Ups", icon: Users, testid: "nav-leads" },
  { to: "/weekly", label: "Weekly Plan", icon: CalendarRange, testid: "nav-weekly" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/content", label: "Content Library", icon: Library, testid: "nav-content" },
  { to: "/copy", label: "Copy Library", icon: FileText, testid: "nav-copy" },
  { to: "/history", label: "Activity History", icon: History, testid: "nav-history" },
  { to: "/onboarding", label: "What We've Done", icon: Compass, testid: "nav-onboarding" },
  { to: "/settings", label: "Campaign Facts", icon: Cog, testid: "nav-settings" },
  { to: "/account", label: "Account / Settings", icon: UserCog, testid: "nav-account" },
];

function NavList({ onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
      {NAV.map((n) => (
        <NavLink
          key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${
              isActive
                ? "bg-[#222736] text-[#F3E5AB] border border-[#D4AF37]/30"
                : "text-[#94A3B8] hover:bg-[#181C27] hover:text-[#F8FAFC]"
            }`
          }
        >
          <n.icon className="w-4 h-4 shrink-0" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const days = daysTo(config?.facts?.departure_date);
  const forced = user && user.must_change_password;

  return (
    <div className="min-h-screen flex bg-[#0A0C10] relative z-10">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 border-r border-[#2A3042] bg-[#0A0C10]/95 backdrop-blur-xl z-40">
        <div className="px-6 py-6 border-b border-[#2A3042]">
          <div className="flex items-center gap-3">
            <img src="/inforno-logo.png" alt="Inforno Travel" className="h-10 w-auto" />
            <div>
              <div className="font-display text-lg leading-none text-[#F8FAFC]">Inforno Travel</div>
              <div className="eyebrow text-[#94A3B8] mt-1">Marketing Director</div>
            </div>
          </div>
        </div>
        <NavList />
        <div className="p-3 border-t border-[#2A3042]">
          <button data-testid="logout-btn" onClick={() => logout().then(() => nav("/login"))}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8] hover:bg-[#181C27] hover:text-[#F8FAFC] w-full">
            <LogOut className="w-4 h-4" /> Sign out ({user?.name})
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-[#0A0C10] border-r border-[#2A3042] flex flex-col" data-testid="mobile-drawer">
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          <div className="px-5 py-5 border-b border-[#2A3042] flex items-center gap-3">
            <img src="/inforno-logo.png" alt="Inforno Travel" className="h-10 w-auto" />
            <div>
              <div className="font-display text-lg leading-none text-[#F8FAFC]">Inforno Travel</div>
              <div className="eyebrow text-[#94A3B8] mt-1">Marketing Director</div>
            </div>
          </div>
          <NavList onNavigate={() => setOpen(false)} />
          <div className="p-3 border-t border-[#2A3042]">
            <button onClick={() => { setOpen(false); logout().then(() => nav("/login")); }}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-[#94A3B8] hover:bg-[#181C27] w-full">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:pl-64 w-full min-w-0">
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8 h-16 border-b border-[#2A3042] bg-[#0A0C10]/85 backdrop-blur-xl">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden p-2 -ml-2 rounded-lg text-[#F8FAFC] hover:bg-[#181C27]" onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu">
              <Menu className="w-6 h-6" />
            </button>
            <div className="min-w-0">
              <div className="font-display text-base sm:text-xl text-[#F8FAFC] truncate">Thailand 2027</div>
              <div className="eyebrow text-[#94A3B8] hidden sm:block">Bangkok + Phuket · June 19–26, 2027</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {days != null && (
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-[#D4AF37]/40 bg-[#181C27]" data-testid="days-badge">
                <Plane className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-mono2 text-[#F3E5AB] text-xs sm:text-sm whitespace-nowrap">{days} DAYS</span>
              </div>
            )}
          </div>
        </header>
        <main className="px-4 lg:px-8 py-6 lg:py-8 max-w-[1400px]">{children}</main>
      </div>

      {/* Forced temporary-password change gate (non-dismissible) */}
      {forced && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="forced-change-dialog">
          <div className="w-full max-w-md card-lux p-6">
            <div className="eyebrow text-[#D4AF37] mb-1">Account security</div>
            <h2 className="font-display text-2xl mb-1 text-[#F8FAFC]">Secure your account</h2>
            <p className="text-sm text-[#94A3B8] mb-4">Set a new private password to continue.</p>
            <ChangePasswordForm forced />
          </div>
        </div>
      )}
    </div>
  );
}
